
<template>
  <div class="collage-carousel">
    <div class="collage-grid">
      <div
        v-for="(image, index) in images"
        :key="index"
        ref="collageItems"
        class="collage-item"
        :class="{ visible: imageVisible[index] }"
        @click="enlargeImage(index)"
      >
        <img
          :src="image"
          class="collage-image"
          :alt="'Imagen ' + index"
        >
      </div>
    </div>

    <!-- Modalasas -->
    <div
      v-if="isImageEnlarged"
      class="image-modal"
      @click="closeModal"
    >
      <img
        :src="images[currentIndex]"
        class="enlarged-image"
      >
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue'
import gsap from 'gsap'

export default {
  name: 'CollageAnimado',
  setup() {
    const images = [
      require('@/assets/foto5.webp'),
    ]

    const currentIndex = ref(0)
    const isImageEnlarged = ref(false)
    const collageItems = ref([])
    const imageVisible = ref(new Array(images.length).fill(false))

    const enlargeImage = (index) => {
      currentIndex.value = index
      isImageEnlarged.value = true
    }

    const closeModal = () => {
      isImageEnlarged.value = false
    }

    const observeImages = () => {
      const options = { threshold: 0.2 } // Un umbral ligeramente menor para activar la magia justo a tiempo

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const index = collageItems.value.indexOf(entry.target)
          if (entry.isIntersecting && !imageVisible.value[index]) {
            imageVisible.value[index] = true
            animateIn(entry.target, index)
          }
        })
      }, options)

      collageItems.value.forEach((el) => observer.observe(el))
    }

    // 🌟 ANIMACIÓN DE REVELADO MÁGICO TRIDIMENSIONAL
    const animateIn = (el, index) => {
      // Calculamos un delay dinámico ultra-suave por si hay múltiples imágenes
      const delayCalculado = 0.2 + index * 0.25

      gsap.fromTo(
        el,
        {
          opacity: 0,
          scale: 0.82,            // Comienza expandiéndose desde el fondo
          y: 70,                  // Eleva sutilmente de abajo hacia arriba
          rotateX: -25,           // Rotación inclinada en perspectiva premium
          rotateY: index % 2 === 0 ? 15 : -15, // Alterna rotación mágica de lado a slide
          filter: 'blur(20px) brightness(1.8) contrast(1.2)', // Destello resplandeciente inicial
        },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          rotateX: 0,
          rotateY: 0,
          filter: 'blur(0px) brightness(1) contrast(1)', // Limpieza cinematográfica del lente
          duration: 1.6,
          delay: delayCalculado,
          ease: 'customPower', // Customizado usando curvas cinéticas premium
          // Usamos una aproximación manual a una curva customizada elástica y fina
          clearProps: 'transform,filter' // Limpia propiedades al terminar para optimizar el hover CSS
        }
      )
    }

    onMounted(async () => {
      await nextTick()
      collageItems.value = collageItems.value.slice(0, images.length)
      observeImages()
    })

    return {
      images,
      currentIndex,
      isImageEnlarged,
      enlargeImage,
      closeModal,
      collageItems,
      imageVisible,
    }
  }
}
</script>

<style scoped>
.collage-carousel {
  width: 100%;
  max-width: 100%;overflow: visible;
  padding: 0; background:
    radial-gradient(ellipse at 50% 20%, rgba(255, 255, 255, 0.95), rgba(253, 242, 243, 0.98)),
    url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="6" height="6"><rect width="6" height="6" fill="white"/><circle cx="3" cy="3" r="0.5" fill="%23f4eae9"/></svg>') repeat;

  margin: 0;
}

.collage-grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 0; background:
    radial-gradient(ellipse at 50% 20%, rgba(255, 255, 255, 0.95), rgba(253, 242, 243, 0.98)),
    url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="6" height="6"><rect width="6" height="6" fill="white"/><circle cx="3" cy="3" r="0.5" fill="%23f4eae9"/></svg>') repeat;

  perspective: 1200px; /* Contenedor con profundidad tridimensional mejorada */
}

.collage-item {
  grid-column: span 12; background:
    radial-gradient(ellipse at 50% 20%, rgba(255, 255, 255, 0.95), rgba(253, 242, 243, 0.98)),
    url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="6" height="6"><rect width="6" height="6" fill="white"/><circle cx="3" cy="3" r="0.5" fill="%23f4eae9"/></svg>') repeat;

  opacity: 0;
  cursor: pointer;
  transform-style: preserve-3d; /* Permite que las caras hijos hereden el espacio 3D */
  will-change: transform, opacity, filter;
}

.collage-item.visible {
  opacity: 1;
}

.collage-image { background:
    radial-gradient(ellipse at 50% 20%, rgba(255, 255, 255, 0.95), rgba(253, 242, 243, 0.98)),
    url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="6" height="6"><rect width="6" height="6" fill="white"/><circle cx="3" cy="3" r="0.5" fill="%23f4eae9"/></svg>') repeat;

  width: 100%;
  height: auto;
  object-fit: cover;
  display: block;
  transition: transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1), filter 0.4s ease;
}

/* Efecto magnético interactivo al pasar el mouse */
.collage-item:hover .collage-image {
  transform: scale(1.035) translateY(-4px);
  filter: drop-shadow(0 15px 30px rgba(225, 180, 185, 0.25));
}

/* Modal */
.image-modal {
  position: fixed;
  inset: 0;
  background-color: rgba(10, 10, 10, 0.94);
  backdrop-filter: blur(8px); /* Blur de fondo premium al abrir el modal */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.enlarged-image {
  max-width: 90%;
  max-height: 90%;
  border-radius: 12px;
  object-fit: contain;
  box-shadow: 0 20px 50px rgba(0,0,0,0.5);
}
</style>

```